import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { formatMoney } from '../../utils/formatters';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

/* Дневной фудкост кухни и бара. Одна цифра за месяц прячет всплески: провал
   на одном дне видно только на ряду. */
const FoodCostTrend = ({ trend = [] }) => {
  if (!trend.length) {
    return <div className="kt-empty">За выбранный период нет продаж</div>;
  }

  const labels = trend.map((day) =>
    new Date(day.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
  );

  /* Один день со списанной в продажу партией даёт фудкост в 2000%, и на такой
     шкале все остальные дни превращаются в прямую у нуля. Поэтому ось режем по
     90-му перцентилю, а про вылетевшие дни честно пишем под графиком. */
  const values = trend
    .flatMap((d) => [d.foodcost, d.kitchen_foodcost, d.bar_foodcost])
    .filter((v) => v > 0)
    .sort((a, b) => a - b);
  const p90 = values.length ? values[Math.floor(values.length * 0.9)] : 0;
  const axisMax = Math.max(60, Math.ceil((p90 * 1.4) / 10) * 10);
  const offScale = trend.filter(
    (d) => d.foodcost > axisMax || d.kitchen_foodcost > axisMax || d.bar_foodcost > axisMax
  );

  const data = {
    labels,
    datasets: [
      {
        label: 'Общий фудкост',
        data: trend.map((d) => d.foodcost),
        borderColor: '#6366f1',
        backgroundColor: 'rgba(99, 102, 241, 0.12)',
        borderWidth: 3,
        tension: 0.3,
        fill: true,
        pointRadius: 2,
        pointHoverRadius: 5
      },
      {
        label: 'Кухня',
        data: trend.map((d) => d.kitchen_foodcost),
        borderColor: '#10b981',
        backgroundColor: 'transparent',
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 5
      },
      {
        label: 'Бар',
        data: trend.map((d) => d.bar_foodcost),
        borderColor: '#f59e0b',
        backgroundColor: 'transparent',
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 5
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8, padding: 16 } },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}%`,
          afterBody: (items) => {
            const day = trend[items[0].dataIndex];
            return `Выручка: ${formatMoney(day.revenue)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: axisMax,
        ticks: { callback: (value) => `${value}%` },
        grid: { color: 'rgba(148, 163, 184, 0.18)' }
      },
      x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkipPadding: 16 } }
    }
  };

  return (
    <>
      <div
        className="kt-chart"
        role="img"
        aria-label="Динамика фудкоста по дням: общий, кухня и бар"
      >
        <Line data={data} options={options} />
      </div>
      {offScale.length > 0 && (
        <div className="kt-scale-note">
          Шкала обрезана на {axisMax}%: {offScale.length}{' '}
          {offScale.length === 1 ? 'день выходит' : 'дн. выходят'} за неё
          ({offScale
            .slice(0, 3)
            .map((d) => `${new Date(d.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })} — ${Math.max(d.foodcost, d.kitchen_foodcost, d.bar_foodcost)}%`)
            .join(', ')}
          {offScale.length > 3 ? ' и др.' : ''}). Причину смотрите на вкладке «Аномалии».
        </div>
      )}
    </>
  );
};

export default FoodCostTrend;
